@if (show) {
<div
  class="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-3 backdrop-blur-md animate-fadeIn sm:p-4"
  (click)="closeModal()">

  <section
    class="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-border-strong bg-surface-modal shadow-modal transition-all duration-300"
    role="dialog"
    aria-modal="true"
    [attr.aria-label]="'ticket.create.title' | translate"
    (click)="$event.stopPropagation()">

    <header class="border-b border-border-subtle bg-surface-elevated px-5 py-4 sm:px-6 sm:py-5">
      <div class="flex items-start justify-between gap-4">
        <div class="flex min-w-0 items-start gap-3">
          <div class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-border-primary bg-primary-soft text-primary shadow-subtle">
            <i class="fa-solid fa-headset text-base"></i>
          </div>

          <div class="min-w-0">
            <div class="mb-2 flex flex-wrap items-center gap-2">
              <span class="badge badge-primary">
                <i class="fa-solid fa-ticket-simple me-1 text-[10px]"></i>
                {{ 'ticket.createNew' | translate }}
              </span>
            </div>
            <h2 class="text-lg font-semibold tracking-tight text-text-primary sm:text-xl">
              {{ 'ticket.create.title' | translate }}
            </h2>
            <p class="mt-1 text-sm leading-6 text-text-secondary">
              {{ 'ticket.create.subtitle' | translate }}
            </p>
          </div>
        </div>

        <button
          type="button"
          (click)="closeModal()"
          class="btn-icon h-9 w-9 shrink-0 border border-border-strong bg-surface text-text-muted hover:border-border-primary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base"
          [attr.aria-label]="'common.cancel' | translate">
          <i class="fa-solid fa-xmark text-sm"></i>
        </button>
      </div>
    </header>

    <div class="overflow-y-auto px-5 py-5 sm:px-6">
      @if (errorMessage) {
        <div class="alert alert-danger mb-5">
          <i class="fa-solid fa-circle-exclamation mt-0.5"></i>
          <p class="leading-6">{{ errorMessage }}</p>
        </div>
      }

      <form [formGroup]="ticketForm" (ngSubmit)="onSubmit()" class="space-y-5">
        <div class="form-group mb-0">
          <label class="label" for="ticket-subject">
            {{ 'ticket.create.subject' | translate }}
            <span class="text-danger">*</span>
          </label>
          <input
            id="ticket-subject"
            type="text"
            formControlName="subject"
            class="form-input"
            dir="auto"
            [placeholder]="'ticket.create.subjectPlaceholder' | translate">
          @if (ticketForm.get('subject')?.touched && ticketForm.get('subject')?.invalid) {
            <p class="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-danger">
              <i class="fa-solid fa-circle-exclamation text-[10px]"></i>
              Subject is required (5-100 chars)
            </p>
          }
        </div>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div class="form-group mb-0">
            <label class="label" for="ticket-department">
              {{ 'ticket.create.department' | translate }}
              <span class="text-danger">*</span>
            </label>
            <div class="relative">
              <select id="ticket-department" formControlName="department" class="form-select cursor-pointer appearance-none pe-10">
                <option value="" disabled>{{ 'ticket.create.selectDepartment' | translate }}</option>
                @for (dept of departments; track dept) {
                  <option [value]="dept">{{ 'ticket.department.' + dept | translate }}</option>
                }
              </select>
              <i class="fa-solid fa-chevron-down pointer-events-none absolute top-1/2 -translate-y-1/2 text-xs text-text-muted ltr:right-4 rtl:left-4"></i>
            </div>
          </div>

          <div class="form-group mb-0">
            <label class="label" for="ticket-category">
              {{ 'ticket.create.category' | translate }}
              <span class="text-danger">*</span>
            </label>
            <div class="relative">
              <select id="ticket-category" formControlName="category" class="form-select cursor-pointer appearance-none pe-10">
                <option value="" disabled>{{ 'ticket.create.selectCategory' | translate }}</option>
                @for (cat of categories; track cat) {
                  <option [value]="cat">{{ 'ticket.category.' + cat | translate }}</option>
                }
              </select>
              <i class="fa-solid fa-chevron-down pointer-events-none absolute top-1/2 -translate-y-1/2 text-xs text-text-muted ltr:right-4 rtl:left-4"></i>
            </div>
          </div>
        </div>

        @if (hasLockedRelatedEntity) {
          <div class="rounded-xl border border-border bg-surface-elevated p-4">
            <p class="text-xs font-bold uppercase tracking-wider text-text-muted">
              {{ 'ticket.create.relatedToType' | translate }}
            </p>
            <div class="mt-2 flex flex-wrap items-center gap-2">
              <span class="badge badge-primary capitalize">
                {{ 'ticket.relatedType.' + defaultRelatedType | translate }}
              </span>
              <span class="font-mono text-xs font-semibold text-text-secondary" dir="ltr">
                {{ defaultRelatedId }}
              </span>
            </div>
          </div>
        } @else {
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div class="form-group mb-0">
              <label class="label" for="ticket-related-type">
                {{ 'ticket.create.relatedToType' | translate }}
                <span class="text-danger">*</span>
              </label>
              <div class="relative">
                <select id="ticket-related-type" formControlName="relatedType" class="form-select cursor-pointer appearance-none pe-10">
                  <option value="" disabled>{{ 'ticket.create.selectRelatedType' | translate }}</option>
                  @for (type of relatedTypes; track type) {
                    <option [value]="type">{{ 'ticket.relatedType.' + type | translate }}</option>
                  }
                </select>
                <i class="fa-solid fa-chevron-down pointer-events-none absolute top-1/2 -translate-y-1/2 text-xs text-text-muted ltr:right-4 rtl:left-4"></i>
              </div>
            </div>

            <div class="form-group mb-0">
              <label class="label" for="ticket-related-id">
                {{ 'ticket.create.relatedToId' | translate }}
                <span class="text-danger">*</span>
              </label>
              <input
                id="ticket-related-id"
                type="text"
                formControlName="relatedId"
                class="form-input font-mono"
                dir="ltr"
                [placeholder]="'ticket.create.relatedIdPlaceholder' | translate">
            </div>
          </div>
        }

        <div class="form-group mb-0">
          <label class="label" for="ticket-description">
            {{ 'ticket.create.description' | translate }}
            <span class="text-danger">*</span>
          </label>
          <textarea
            id="ticket-description"
            formControlName="description"
            rows="5"
            class="form-textarea resize-none"
            dir="auto"
            [placeholder]="'ticket.create.descriptionPlaceholder' | translate"></textarea>
          @if (ticketForm.get('description')?.touched && ticketForm.get('description')?.invalid) {
            <p class="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-danger">
              <i class="fa-solid fa-circle-exclamation text-[10px]"></i>
              Please provide more details (min 20 chars)
            </p>
          }
        </div>
      </form>
    </div>

    <footer class="flex flex-col-reverse gap-3 border-t border-border-subtle bg-surface-elevated px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
      <button type="button" (click)="closeModal()" class="btn btn-secondary min-h-11 w-full sm:w-auto">
        <i class="fa-solid fa-xmark text-xs"></i>
        {{ 'common.cancel' | translate }}
      </button>
      <button
        type="button"
        (click)="onSubmit()"
        [disabled]="ticketForm.invalid || isSubmitting"
        class="btn btn-primary min-h-11 w-full sm:w-auto">
        @if (isSubmitting) {
          <span class="loader h-4 w-4"></span>
          {{ 'common.submitting' | translate }}
        } @else {
          <i class="fa-solid fa-paper-plane text-xs"></i>
          {{ 'ticket.create.submit' | translate }}
        }
      </button>
    </footer>
  </section>
</div>
}
